import type { ArrayIterator, Many } from "lodash";
import {
  ary,
  first,
  flatMapDeep,
  get,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  keys,
  last,
  map,
  omit,
  omitBy,
  partial,
  partialRight,
  pick,
  some,
  sortBy,
  stubTrue,
  tail,
  values,
  xor,
} from "lodash";
import pMap from "p-map";
import type { Predicate } from "./type-utils";

/**
 * Returns items that appear only once in the array.
 *
 * Example:
 * ```ts
 * const result = uniques(["a", "b", "c", "b", "e", "c", "b"]);
 * expect(result).toEqual(["a", "e"]);
 * ```
 */
export function uniques<T>(items: T[]): T[] {
  return xor(...items.map((a) => [a]));
}

/**
 * Returns items that appear more than once in the array.
 *
 * Example:
 * ```ts
 * const result = duplicates(["a", "b", "c", "b", "e", "c", "b"]);
 * expect(result).toEqual(["b", "c"]);
 * ```
 */
export function duplicates<T>(items: T[]): T[] {
  return xor(items, uniques(items));
}

/**
 * {@link _.flatMapDeep} that works properly.
 *
 * `flatMapDeep` is basically an alias for `map`+`flattenDeep`. If you were looking for a method that would
 * recursively apply the `map` part and then flatten the result, then this would be it.
 */
export function flatMapSuperDeep<T>(items: T[], iteratee: (_: T) => T[]): T[] {
  const flatten = (x: T): [T, T[]] => [x, flatMapDeep(iteratee(x), flatten)];
  return flatMapDeep(items, flatten);
}

/**
 * Returns the first element in the array.
 *
 * Example 1:
 * ```ts
 * const result = first(["a", "b", "c"]);
 * expect(result).toEqual("a");
 * ```
 *
 * Example 2:
 * ```ts
 * const result = first([]);
 * expect(result).toBeNull();
 * ```
 *
 * Example 3:
 * ```ts
 * const result = first(["a", "b", "b", "c"], (element) => element === "b");
 * expect(result).toEqual("b");
 * ```
 *
 * Example 4:
 * ```ts
 * const result = first(["a", "b", "c"], (element) => element === "d");
 * expect(result).toBeNull();
 * ```
 *
 * @throws `Error` if the array is `undefined` or `null`.
 * @param items the array to get the first element from.
 * @returns the first element that was found.
 */
export function firstOrDefault<T>(items: T[], predicate?: Predicate<T>): T | null {
  if (items === undefined) {
    throw new Error("Expected an array, but got undefined.");
  }

  if (items === null) {
    throw new Error("Expected an array, but got null.");
  }

  const filtered = items.filter(predicate ?? stubTrue);
  if (filtered.length === 0) {
    return null;
  }

  return first(filtered);
}

/**
 * Returns the last element in the array.
 *
 * Example 1:
 * ```ts
 * const result = last(["a", "b", "c"]);
 * expect(result).toEqual("c");
 * ```
 *
 * Example 2:
 * ```ts
 * const result = last([]);
 * expect(result).toBeNull();
 * ```
 *
 * Example 3:
 * ```ts
 * const result = last(["a", "b", "b", "c"], (element) => element === "b");
 * expect(result).toEqual("b");
 * ```
 *
 * Example 4:
 * ```ts
 * const result = last(["a", "b", "c"], (element) => element === "d");
 * expect(result).toBeNull();
 * ```
 *
 * @throws `Error` if the array is `undefined` or `null`.
 * @param items the array to get the last element from.
 * @returns the last element that was found.
 */
export function lastOrDefault<T>(items: T[], predicate?: Predicate<T>): T | null {
  if (items === undefined) {
    throw new Error("Expected an array, but got undefined.");
  }

  if (items === null) {
    throw new Error("Expected an array, but got null.");
  }

  const filtered = items.filter(predicate ?? stubTrue);
  if (filtered.length === 0) {
    return null;
  }

  return last(filtered);
}

/**
 * Returns the only element in the array.
 *
 * Example 1:
 * ```ts
 * const result = single(["a"]);
 * expect(result).toEqual("a");
 * ```
 *
 * Example 2:
 * ```ts
 * const result = single(["a", "b", "c"], (element) => element === "a");
 * expect(result).toEqual("a");
 * ```
 *
 * Example 3:
 * ```ts
 * const func = (): string => single(["a", "b"]);
 * expect(func).toThrow("Expected exactly one element. but got 2 elements.");
 * ```
 *
 * @throws `Error` if the array is `undefined`, `null`, or when more than one element is found.
 * @param items the array to get the only element from.
 * @param predicate an optional predicate to filter the array.
 * @returns the single element that was found.
 */
export function single<T>(items: T[], predicate?: Predicate<T>): T {
  if (items === undefined) {
    throw new Error("Expected exactly one element, but got an undefined array.");
  }

  if (items === null) {
    throw new Error("Expected exactly one element, but got a null array.");
  }

  const filtered = items.filter(predicate ?? stubTrue);
  if (filtered.length === 1) {
    return first(filtered);
  }

  throw new Error(`Expected exactly one element. but got ${filtered.length} elements.`);
}

/**
 * Returns the only element in the array.
 *
 * Example 1:
 * ```ts
 * const result = single(["a"]);
 * expect(result).toEqual("a");
 * ```
 *
 * Example 2:
 * ```ts
 * const result = single(["a", "b", "c"], (element) => element === "a");
 * expect(result).toEqual("a");
 * ```
 *
 * Example 3:
 * ```ts
 * const result = singleOrDefault(["b", "c", "d"], (element) => element === "a");
 * expect(result).toBeNull();
 * ```
 *
 * Example 4:
 * ```ts
 * const func = (): string => single(["a", "a", "b"], (element) => element === "a";
 * expect(func).toThrow("Expected exactly one element. but got 2 elements.");
 * ```
 *
 * @throws `Error` if the array is `undefined`, `null`, or when more than one element is found.
 * @param items the array to get the only element from.
 * @param predicate an optional predicate to filter the array.
 * @returns the single element that was found, or `null` if no element was found
 */
export function singleOrDefault<T>(items: T[], predicate?: Predicate<T>): T | null {
  if (items === undefined) {
    throw new Error("Expected exactly one element, but got an undefined array.");
  }

  if (items === null) {
    throw new Error("Expected exactly one element, but got a null array.");
  }

  const filtered = items.filter(predicate ?? stubTrue);
  if (filtered.length === 0) {
    return null;
  }

  if (filtered.length === 1) {
    return first(filtered);
  }

  throw new Error(`Expected exactly one element. but got ${filtered.length} elements.`);
}

/**
 * Returns whether the array has exactly one element.
 *
 * Example 1:
 * ```ts
 * const result = hasOne(["a"]);
 * expect(result).toBeTrue();
 * ```
 *
 * Example 2:
 * ```ts
 * const result = hasOne(["a", "b", "c"], (element) => element === "a");
 * expect(result).toBeTrue();
 * ```
 *
 * Example 3:
 * ```ts
 * const result = hasOne(["a", "b", "c"], (element) => element === "d");
 * expect(result).toBeFalse();
 * ```
 *
 * Example 4:
 * ```ts
 * const result = hasOne(["a", "b", "c"]);
 * expect(result).toBeFalse();
 * ```
 *
 * Example 5:
 * ```ts
 * const result = hasOne([]);
 * expect(result).toBeFalse();
 * ```
 *
 * Example 6:
 * ```ts
 * const result = hasOne(null);
 * expect(result).toBeFalse();
 * ```
 *
 * @param items the array to check.
 * @param predicate an optional predicate to filter the array.
 * @returns `true` if the array has exactly one element, `false` otherwise.
 **/
export function hasOne<T>(items: T[], predicate?: Predicate<T>): boolean {
  return (items ?? []).filter(predicate ?? stubTrue).length === 1;
}

/**
 * Returns whether the array has more than one element.
 *
 * Example 1:
 * ```ts
 * const result = hasMany(["a", "b"]);
 * expect(result).toBeTrue();
 * ```
 *
 * Example 2:
 * ```ts
 * const result = hasMany(["a", "b", "c"], (element) => element === "a");
 * expect(result).toBeFalse();
 * ```
 *
 * Example 3:
 * ```ts
 * const result = hasMany(["a", "b", "c"], (element) => element === "d");
 * expect(result).toBeFalse();
 * ```
 *
 * Example 4:
 * ```ts
 * const result = hasMany(["a"]);
 * expect(result).toBeFalse();
 * ```
 *
 * Example 5:
 * ```ts
 * const result = hasMany([]);
 * expect(result).toBeFalse();
 * ```
 *
 * Example 6:
 * ```ts
 * const result = hasMany(null);
 * expect(result).toBeFalse();
 * ```
 *
 * @param items the array to check.
 * @param predicate an optional predicate to filter the array.
 * @returns `true` if the array has more than one element, `false` otherwise.
 **/
export function hasMany<T>(items: T[], predicate?: Predicate<T>): boolean {
  return (items ?? []).filter(predicate ?? stubTrue).length > 1;
}

/**
 * Returns whether the array has more than one element.
 *
 * Example 1:
 * ```ts
 * const result = hasMoreThanOne(["a", "b", "c"]);
 * expect(result).toBeTrue();
 * ```
 *
 * Example 2:
 * ```ts
 * const result = hasMoreThanOne(["a", "b", "c"], (element) => element === "a");
 * expect(result).toBeFalse();
 * ```
 *
 * Example 3:
 * ```ts
 * const result = hasMoreThanOne(["a", "b", "a", "c"], (element) => element === "a");
 * expect(result).toBeTrue();
 * ```
 *
 * Example 4:
 * ```ts
 * const result = hasMoreThanOne(["a", "b", "c"], (element) => element === "d");
 * expect(result).toBeFalse();
 * ```
 *
 * Example 5:
 * ```ts
 * const result = hasMoreThanOne(["a"]);
 * expect(result).toBeFalse();
 * ```
 *
 * Example 6:
 * ```ts
 * const result = hasMoreThanOne([]);
 * expect(result).toBeFalse();
 * ```
 *
 * Example 7:
 * ```ts
 * const result = hasMoreThanOne(null);
 * expect(result).toBeFalse();
 * ```
 *
 * @param items the array to check.
 * @param predicate an optional predicate to filter the array.
 * @returns `true` if the array has more than one element, `false` otherwise.
 */
export function hasMoreThanOne<T>(items: T[], predicate?: Predicate<T>): boolean {
  return (items ?? []).filter(predicate ?? stubTrue).length > 1;
}

/**
 * Picks properties from each item of array.
 *
 * Equivalent to `items.map(x => pick(x, props))`.
 *
 * Example:
 * ```ts
 * const result = pickEach([{ n: 1 }, { n: 2, o: 3 }], "n");
 * expect(result).toEqual([{ n: 1 }, { n: 2 }]);
 * ```
 *
 * @see {@link omitEach}
 */
export function pickEach<T, K extends keyof T>(items: T[], ...props: Many<K>[]): Pick<T, K>[] {
  return map(items, <ArrayIterator<T, Pick<T, K>>>partialRight(pick, ...props));
}

/**
 * Omits properties from each item of array.
 *
 * Equivalent to `items.map(x => omit(x, props))`.
 *
 * Example:
 * ```ts
 * const result = omitEach([{ n: 1, o: 6 }, { n: 2 }], "o");
 * expect(result).toEqual([{ n: 1 }, { n: 2 }]);
 * ```
 *
 * @see {@link pickEach}
 */
export function omitEach<T, K extends keyof T>(items: T[], ...props: Many<K>[]): Omit<T, K>[] {
  return map(items, <ArrayIterator<T, Omit<T, K>>>partialRight(omit, ...props));
}

/**
 * Checks if arrays intersect.
 *
 * Equivalent to `intersection(left, right).length > 0`.
 *
 * Example:
 * ```ts
 * const result = intersects([2, 3], [3]);
 * expect(result).toBeTrue();
 * ```
 */
export function intersects<T>(left: T[], right: T[]): boolean {
  return some(left, ary(partial(includes, right), 1));
}

/**
 * Checks whether two arrays contain the same elements, regardless of order, as determined by the
 * key returned by `iteratee` for each element.
 *
 * Useful as a `distinctUntilChanged` comparator when only element identity (e.g. an `id` field)
 * matters, not the full element content, and to avoid re-emitting when an array was rebuilt
 * (new reference, e.g. from a `.map()`/`.filter()` chain) but its contents did not actually change.
 *
 * Example:
 * ```ts
 * const result = arrayEqualsBy([{ id: 1 }, { id: 2 }], [{ id: 2 }, { id: 1 }], (x) => x.id);
 * expect(result).toBeTrue();
 * ```
 */
export function arrayEqualsBy<T, K>(left: T[], right: T[], iteratee: (item: T) => K): boolean {
  if (left === right) {
    return true;
  }

  if (isNil(left) || isNil(right)) {
    return false;
  }

  if (left.length !== right.length) {
    return false;
  }

  return isEqual(sortBy(map(left, iteratee)), sortBy(map(right, iteratee)));
}

/**
 * Normalizes a value that may be a single item or an array of items into an array.
 * Useful for data that was historically stored as a scalar but is now expected to be an array.
 *
 * Example 1:
 * ```ts
 * const result = asArray([1, 2, 3]);
 * expect(result).toEqual([1, 2, 3]);
 * ```
 *
 * Example 2:
 * ```ts
 * const result = asArray(1);
 * expect(result).toEqual([1]);
 * ```
 *
 * Example 3:
 * ```ts
 * const result = asArray(null);
 * expect(result).toEqual([]);
 * ```
 */
export function asArray<T>(value: T | T[] | null | undefined): T[] {
  return isNil(value) ? [] : Array.isArray(value) ? value : [value];
}

/**
 * Checks if the given index exists in the array.
 *
 * Example:
 * ```ts
 * const result = hasIndex(["a", "b", "c"], 1);
 * expect(result).toBeTrue();
 * ```
 *
 * Example:
 * ```ts
 * const result = hasIndex(["a", "b", "c"], 3);
 * expect(result).toBeFalse();
 * ```
 *
 * Example:
 * ```ts
 * const result = hasIndex(null, 0);
 * expect(result).toBeFalse();
 * ```
 */
export function hasIndex<T>(array: T[] | null, index: number): boolean {
  return Array.isArray(array) && index >= 0 && index < array.length;
}

/**
 * Checks if value is empty. A value is considered empty unless it’s an arguments object, array, string, or
 * jQuery-like collection with a length greater than 0 or an object with own enumerable properties that are not undefined or null.
 *
 * @param value The value to inspect.
 * @return Returns true if value is empty, else false.
 */
export function isTechnicallyEmpty(value: object): boolean {
  return isEmpty(omitBy(value, isNil));
}

interface Group<TKey, TEntry> {
  readonly key: TKey;
  readonly entries: TEntry[];
}

/**
 * Groups entries in a collection based on the key returned by the provided selector function.
 *
 * @param collection - The array of entries to group.
 * @param keySelector - A function that extracts a key from each entry.
 * @returns An array of grouped entries, where each group contains a unique key and its corresponding entries.
 */
export function typedGroupBy<TEntry, TKey extends string | number | symbol>(
  collection: TEntry[],
  keySelector: (entry: TEntry) => TKey
): Group<TKey, TEntry>[] {
  const fixedCollection = collection ?? [];
  const seed = {} as Record<TKey, Group<TKey, TEntry>>;

  const record = fixedCollection.reduce((result, nextValue) => {
    const groupKey = keySelector(nextValue);
    const existingGroup = result[groupKey];

    const updatedGroup: Group<TKey, TEntry> = !isNil(existingGroup)
      ? { key: groupKey, entries: [...existingGroup.entries, nextValue] }
      : { key: groupKey, entries: [nextValue] };

    return { ...result, [groupKey]: updatedGroup };
  }, seed);

  return values(record);
}

/**
 * A type-safe version of `Object.keys`.
 *
 * Example:
 * ```ts
 * const obj = { a: 1, b: 2 };
 * const keys = typedKeys(obj); // keys is of type ("a" | "b")[]
 * ```
 *
 * @param obj The object to get the keys from.
 * @returns The keys of the object.
 */
export function typedKeys<T extends string | number | symbol>(obj: Partial<Record<T, unknown>>): T[] {
  return keys(obj) as T[];
}

/**
 * Replaces an item in the array if it exists, or adds it if it does not.
 * If the item exists, it will be replaced with the new item.
 * If the item does not exist, it will be added to the end of the array.
 *
 * Example 1:
 * ```ts
 * const array = [{ id: 1, name: "Alice" }, { id: 2, name: "Bob" }];
 * const newItem = { id: 2, name: "Charlie" };
 * const result = replaceOrAdd(array, newItem, (item) => item.id === newItem.id);
 * // Produces: [{ id: 1, name: "Alice" }, { id: 2, name: "Charlie" }]
 * ```
 *
 * Example 2:
 * ```ts
 * const array = [{ id: 1, name: "Alice" }, { id: 2, name: "Bob" }];
 * const newItem = { id: 3, name: "Charlie" };
 * const result = replaceOrAdd(array, newItem, (item) => item.id === newItem.id);
 * // Produces: [{ id: 1, name: "Alice" }, { id: 2, name: "Bob" }, { id: 3, name: "Charlie" }]
 * ```
 *
 * @param array The array to modify.
 * @param item The item to replace or add.
 * @param predicate A function that determines if an item in the array matches the item to replace.
 * @returns A new array with the item replaced or added.
 */
export function replaceOrAdd<T>(array: T[], item: T, predicate: (item: T) => boolean): T[] {
  const foundIndex = array.findIndex(predicate);
  return foundIndex > -1
    ? array.map((existingItem, index) => (index === foundIndex ? item : existingItem))
    : [...array, item];
}

/*
 * This function is used to get values from a path that may contain arrays.
 * It will return an array of values that are found in the path.
 *
 * Example:
 *
 * const obj = {
 *   a: [
 *     { b: 1 },
 *     { b: 2 }
 *   ]
 * }
 *
 * getValuesFromPathWithArrays(obj, "a.b") // [1, 2]
 *
 * @param objectWithPossibleArraysInside The object that may contain arrays.
 * @param path The path to get the values from.
 */
export function getValuesFromPathWithArrays<T>(objectWithPossibleArraysInside: any, path: string): T[] {
  const paths = traverseAndSpawn(objectWithPossibleArraysInside, path);
  return paths.map((path) => get(objectWithPossibleArraysInside, path));
}

function traverseAndSpawn(obj: any, currentPath: string): string[] {
  const splittedPath = currentPath.split(".");
  const firstPart = first(splittedPath);
  const remainingPath = tail(splittedPath);
  const value = get(obj, firstPart);
  if (isArray(value)) {
    const arrayResult = [];
    for (let i = 0; i < value.length; i++) {
      arrayResult.push(
        traverseAndSpawn(value, [i.toString(), ...remainingPath].join(".")).map((path) => `${firstPart}.${path}`)
      );
    }
    return arrayResult.flat();
  } else {
    if (remainingPath.length > 0) {
      return traverseAndSpawn(value, remainingPath.join(".")).map((path) => `${firstPart}.${path}`);
    } else {
      return [firstPart];
    }
  }
}

/**
 * Filters an array using an async predicate function.
 * @param items The array to filter
 * @param predicate An async function that returns a Promise<boolean>
 * @returns A Promise that resolves to the filtered array
 */
export async function filterAsync<T>(
  items: T[],
  predicate: (item: T, index: number) => Promise<boolean>
): Promise<T[]> {
  const promises = items.map(async (item, index) => {
    const shouldInclude = await predicate(item, index);
    return { item, shouldInclude };
  });

  const results = await Promise.all(promises);
  return results.filter(({ shouldInclude }) => shouldInclude).map(({ item }) => item);
}

/**
 * Maps items sequentially (concurrency: 1) using the given async mapper.
 *
 * @param items The items to map
 * @param mapper An async function to apply to each item
 * @returns A Promise that resolves to the mapped results
 */
export async function sequential<TInput, TOutput>(
  items: TInput[],
  mapper: (item: TInput) => Promise<TOutput>
): Promise<TOutput[]> {
  return pMap(items, mapper, { concurrency: 1 });
}
